import * as React from 'react';
import { Maybe } from 'types/generated';
import { Heading, Paragraph, Subheading } from 'components/src/typography';
import Button, { variantFromString } from 'components/src/button';
import { FooterFieldsFragment } from '../../../@types/generated';
import { fullScreenChildGridClasses, centeredFooterGridClasses } from '@/styles/grid';
import { formatLink } from '@/utils/utils';
import { FacebookIcon, YoutubeIcon, TiktokIcon, LinkedInIcon, InstagramIcon, Logo } from 'components/src/svg';
import Link from 'next/link';
import Image from 'next/image';
import typewriter, { Placement, Title } from '@/analytics/segment';
import { useSetAtom } from 'jotai';
import { isDoBusinessWithUsFormAtom } from '@/store';
import { useRouter } from 'next/router';

export type FooterProps = {
  data: FooterFieldsFragment;
  skipCta?: Maybe<boolean>;
};

const renderLinks = (data: FooterFieldsFragment) => {
  const linkCategories = data?.linkSectionsCollection?.items.map((linkSection) => {
    if (linkSection?.title) {
      const links = linkSection?.childrenCollection?.items.map((item) => {
        if (item?.uri && item?.title) {
          if (item?.__typename === 'Link') {
            return (
              <Link key={`footer-link-title-${item?.title}`} className="body-lg" href={formatLink(item.uri)}>
                {item?.title}
              </Link>
            );
          } else if (item?.__typename === 'NavbarLinkWithDescription' && item?.icon?.url) {
            return (
              <div key={`footer-desc-link-section-${item?.title}`} className="my-8 flex flex-col gap-8">
                <Paragraph key={`footer-desc-link-title-${item?.title}`} classes="body-lg text-white-100">
                  {item?.description}
                </Paragraph>
                <div className="grid grid-flow-col justify-start gap-x-8">
                  <Image
                    key={`footer-desc-link-img-${item?.title}`}
                    className="self-center"
                    src={item?.icon.url}
                    alt="headphone-icon"
                    width="24"
                    height="24"
                  />
                  <Link
                    key={`footer-desc-link-subsection-${item?.title}`}
                    className="font-underscore my-auto !font-bold text-white-100"
                    href={item?.uri}
                  >
                    <Paragraph classes="label-lg pt-1 text-white-100">{item?.title}</Paragraph>
                  </Link>
                </div>
              </div>
            );
          }
        }
        return null;
      });
      return (
        <div key={`footer-link-div-${linkSection?.title}`} className="grid gap-y-8">
          <Subheading key={`footer-link-${linkSection?.title}`} classes="title-lg mb-8 text-white-100">
            {linkSection?.title}
          </Subheading>
          {links}
        </div>
      );
    }
    return null;
  });
  return (
    <div
      key={`footer-link-section`}
      className="col-span-12 my-32 grid items-start gap-x-24 gap-y-32 text-start text-white-100 md:col-span-9 md:my-16 md:mb-0 md:mt-0 md:grid-cols-2 lg:grid-cols-4"
    >
      {linkCategories}
    </div>
  );
};

const Footer = ({ data, skipCta }: FooterProps) => {
  const router = useRouter();
  const setIsDoBusinessWithUsForm = useSetAtom(isDoBusinessWithUsFormAtom);
  let rootCss = fullScreenChildGridClasses;

  const firstSlug = React.useMemo(() => {
    const path = (router.asPath || '/').split('?')[0].split('#')[0]; // strip query/hash
    const [first] = path.split('/').filter(Boolean);
    return first ?? null;
  }, [router.asPath]);

  const isArsskyrsla = firstSlug === 'arsskyrsla';

  if (skipCta) {
    rootCss = 'md:gap-x-24 grid w-full md:grid-cols-root-md col-span-full';
  }
  if (isArsskyrsla) {
    return (
      <footer className="col-span-full w-full bg-red-100">
        <div className="mx-auto flex min-h-[240px] max-w-screen-xl flex-col items-center justify-center px-8 py-56 text-center md:py-64">
          <Logo variant="inverted" size={100} />
          <h3 className="text-white-100">Árs- & sjálfbærniskýrsla 2025</h3>
        </div>
      </footer>
    );
  } else {
    return (
      <div className={`${rootCss} h-fit bg-red-100 px-8 pt-56 md:px-0 md:pt-0 `}>
        <div className={`${centeredFooterGridClasses} mb-32 grid-flow-row md:my-64 md:gap-y-32`}>
          <div className="col-span-12 grid gap-x-24 gap-y-16 border-b-1 border-[#FFF]/30 pb-big md:grid-flow-col md:gap-y-0">
            <div className="col-span-12 space-y-[20px] md:col-span-3 md:justify-self-start">
              <Logo variant="inverted" size={100} />
              <div className="space-y-8">
                <Paragraph key="follow-tagline" classes="body-lg text-white-100 md:text-start">
                  {'Fylgdu okkur'}
                </Paragraph>
                <div className="grid w-fit grid-flow-col justify-items-center gap-x-16 gap-y-8 py-sm md:justify-items-start">
                  <Link
                    href={'https://www.facebook.com/Syn.island'}
                    className="hover:cursor-pointer"
                    aria-label="Facebook"
                  >
                    <FacebookIcon />
                  </Link>
                  <Link
                    href={'https://www.youtube.com/@synisland'}
                    className="hover:cursor-pointer"
                    aria-label="YouTube"
                  >
                    <YoutubeIcon />
                  </Link>
                  <Link
                    href={'https://www.tiktok.com/@syn.island'}
                    className="hover:cursor-pointer"
                    aria-label="TikTok"
                  >
                    <TiktokIcon />
                  </Link>
                  <Link
                    href={'https://www.linkedin.com/company/syn-hf'}
                    className="hover:cursor-pointer"
                    aria-label="LinkedIn"
                  >
                    <LinkedInIcon />
                  </Link>
                  <Link
                    href={'https://www.instagram.com/syn.island/'}
                    className="hover:cursor-pointer"
                    aria-label="Instagram"
                  >
                    <InstagramIcon />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-span-12 grid gap-[28px] pt-32 md:col-span-5">
              {!skipCta && (
                <div className="col-span-12 space-y-32 text-start">
                  {data?.heading && (
                    <Heading key="footer-heading" classes="!leading-none text-white-100" variant="xl">
                      {data.heading}
                    </Heading>
                  )}
                  {data?.subheading && (
                    <Paragraph key="footer-subheading" classes="!leading-7 text-white-100">
                      {data.subheading}
                    </Paragraph>
                  )}
                </div>
              )}
              {!skipCta && data?.ctaButton?.label && (
                <div className="col-span-12 self-end md:col-span-6">
                  <Button
                    label={data.ctaButton?.label}
                    labelClasses="text-red-100"
                    variant={variantFromString(data.ctaButton?.variant)}
                    onClick={() => {
                      typewriter.doBusinessWithUsButtonClicked({
                        placement: Placement.Footer,
                        title: Title.KomduÍViðskipti,
                      });
                      if (data.contactForm) {
                        setIsDoBusinessWithUsForm(true);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          {renderLinks(data)}
          <div className="col-span-12 flex flex-col-reverse items-center gap-32 pt-32 text-center md:flex-row md:items-end md:justify-between md:text-start">
            {data?.companyInfo && (
              <div>
                <Paragraph key="footer-company-info" classes="text-white-75" variant="small">
                  {data?.companyInfo}
                </Paragraph>
              </div>
            )}
            <div className="flex flex-row flex-wrap gap-24">
              {data.certificationsCollection?.items.map((asset, index) => {
                return (
                  <Image
                    key={`certification-asset-${index}`}
                    src={asset?.url || ''}
                    alt="certificate"
                    width={80}
                    height={64}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default Footer;
